
import React, { useEffect, useMemo, useRef, useCallback, forwardRef, useImperativeHandle } from "react";

import { useAssistance } from "../../hooks";
import "./PlayerCard.css"
import { OrbitControls } from '@react-three/drei';
import { useThree } from "@react-three/fiber";
import { OpaJag } from "../common/3d/OpaJag";
import { Parking } from "../common/3d/Parking";

import { ParkingUS } from "../common/3d/ParkingUS";
import { NeutralCar } from "../common/3d/Neutral_car";
import * as THREE from 'three';

import { gsap } from "gsap"

// DirectionalRotationPlugin start
// from: https://gsap.com/community/forums/topic/31499-gsap3-short-rotation/
gsap.registerPlugin({
    name: "directionalRotation",
    init(target, values) {
        if (typeof (values) !== "object") {
            values = { rotation: values };
        }
        var data = this,
            cap = values.useRadians ? Math.PI * 2 : 360,
            min = 1e-6,
            p, v, start, end, dif, split;
        data.endValues = {};
        data.target = target;
        for (p in values) {
            if (p !== "useRadians") {
                end = values[p];
                split = (end + "").split("_");
                v = split[0];
                start = parseFloat(target[p]);
                end = data.endValues[p] = (typeof (v) === "string" && v.charAt(1) === "=") ? start + parseInt(v.charAt(0) + "1", 10) * Number(v.substr(2)) : +v || 0;
                dif = end - start;
                if (split.length) {
                    v = split.join("_");
                    if (~v.indexOf("short")) {
                        dif = dif % cap;
                        if (dif !== dif % (cap / 2)) {
                            dif = (dif < 0) ? dif + cap : dif - cap;
                        }
                    }
                    if (v.indexOf("_cw") !== -1 && dif < 0) {
                        dif = ((dif + cap * 1e10) % cap) - ((dif / cap) | 0) * cap;
                    } else if (v.indexOf("ccw") !== -1 && dif > 0) {
                        dif = ((dif - cap * 1e10) % cap) - ((dif / cap) | 0) * cap;
                    }
                }
                if (dif > min || dif < -min) {
                    data.add(target, p, start, start + dif);
                    data._props.push(p);
                }
            }
        }
    },
    render(progress, data) {
        if (progress === 1) {
            for (let p in data.endValues) {
                data.target[p] = data.endValues[p];
            }
        } else {
            let pt = data._pt;
            while (pt) {
                pt.r(progress, pt.d);
                pt = pt._next;
            }
        }
    }
});
// DirectionalRotationPlugin end.

const useNormalizePositionData = () => {
    const initialDeltaRef = useRef(null);

    const normalizePositionData = useCallback((crashData, vehicleKey, initialPos = [0, 0, 0], initialRot = [0, 0, 0]) => {
        if (!crashData?.[vehicleKey]?.pos_0) return { xPosition: [], yPosition: [], zPosition: [], yawPosition: [], pitchPosition: [], rollPosition: [] };

        const data = crashData[vehicleKey];

        // If initialDeltaRef is not set, calculate and store the initial delta
        if (!initialDeltaRef.current) {
            initialDeltaRef.current = {
                xDelta: data.pos_0[0] - initialPos[0],
                yDelta: data.pos_2[0] - initialPos[1],
                zDelta: data.pos_1[0] - initialPos[2],
                yawDelta: data.pos_4[0] - initialRot[0],
                pitchDelta: data.pos_3[0] - initialRot[1],
                rollDelta: data.pos_5[0] - initialRot[2],
            };
        }

        const { xDelta, yDelta, zDelta, yawDelta, pitchDelta, rollDelta } = initialDeltaRef.current;

        return {
            xPosition: data.pos_0.map(x => -1 * (x - xDelta)).map(x => x - 1.3),
            yPosition: data.pos_2.map(y => y - yDelta),
            zPosition: data.pos_1.map(z => z - zDelta),
            yawPosition: data.pos_4.map(yaw => yaw - yawDelta),
            pitchPosition: data.pos_3.map(pitch => pitch - pitchDelta),
            rollPosition: data.pos_5.map(roll => roll - rollDelta),
        };
    }, []);

    return normalizePositionData;
};

const SceneViewer = forwardRef(({ timeline, ...props }, ref) => {
    const { crashData, assistance } = useAssistance();
    const set = useThree((state) => state.set);
    const { camera: customCamera } = useThree();
    const jaguarRef = useRef();
    const otherJaguarRef = useRef();
    const controlsRef = useRef();

    const scenarioLocation = useMemo(() => ["VMTC_NO_CRASH", "VMTC_CRASH", "1", "2", "5"].includes(assistance?.scenario_id) ? "VMTC" : "CES", [assistance?.scenario_id])
    const initialPosition = useMemo(() => scenarioLocation === "VMTC" ? [11.5, 1.2, 10.4] : [-12, 0, -8], [scenarioLocation]);
    const initialRotation = useMemo(() => scenarioLocation === "VMTC" ? [0, -Math.PI / 16, 0] : [0, Math.PI / 2, 0], [scenarioLocation]);
    const initialCameraPosition = scenarioLocation === "VMTC" ? [-20, 0, 0] : [5, 0, -5]

    useEffect(() => {
        const customCamera = new THREE.PerspectiveCamera(50, 4, 0.1, 1000);
        if (scenarioLocation === "VMTC") {
            customCamera.position.set(-25, 20, 15);
        }
        else {
            customCamera.position.set(14.4, 9.58, -5.6);
        }
        set({ camera: customCamera });

    }, [set, scenarioLocation]);

    useImperativeHandle(ref, () => ({
        killAllCurrentCameraTweens: () => {
            // console.log(timeline.getChildren())
            const allCameraTweens = timeline.getTweensOf(customCamera.position)
            allCameraTweens.forEach((tween) => {
                if (tween.time() !== 0) {
                    tween.pause();
                }
            });
        },
    }));
    // useFrame(() => {
    //     console.log(assistance.scenario_id)

    //     if (customCamera) {
    //         // console.log(customCamera.position)

    //     }

    // },[timeline])
    const normalizePositionData = useNormalizePositionData();

    const vehicle0Data = useMemo(() =>
        normalizePositionData(crashData, 'vehicle0', initialPosition, initialRotation),
        [crashData, normalizePositionData, initialPosition, initialRotation]
    );
    const vehicle1Data = useMemo(() =>
        normalizePositionData(crashData, 'vehicle1'),
        [crashData, normalizePositionData]
    );

    const animateVehicle = useCallback((ref, otherRef, positionData, positionDataOther) => {
        const animateVehiclePositionAndRotation = (timeline, targetRef, position, rotation, label = '<') => {
            timeline.to(targetRef.current.position, {
                x: position.x,
                y: position.y,
                z: position.z,
            }, label);

            timeline.to(targetRef.current.rotation, {
                directionalRotation: {
                    x: `${rotation.yaw}_short`,
                    y: `${rotation.pitch}_short`,
                    z: `${rotation.roll}_short`,
                    useRadians: true,
                },
            }, "<");
        };

        const animateCamera = (timeline, cameraPosition, duration, label) => {
            timeline.to(customCamera.position, {
                x: cameraPosition.x,
                y: cameraPosition.y,
                z: cameraPosition.z,
                duration,
            }, label);
        };

        const updateTarget = (timeline, position, label) => {
            timeline.to({}, {
                duration: 0,
                onUpdate: () => {
                    const { x, y, z } = position;
                    controlsRef.current.target.set(x, y, z);
                    controlsRef.current.update();

                }
            }, label);
        };

        // Animate each position and rotation for both vehicles
        positionData.xPosition.forEach((_, index) => {
            animateVehiclePositionAndRotation(
                timeline,
                ref,
                {
                    x: positionData.xPosition[index],
                    y: positionData.yPosition[index],
                    z: positionData.zPosition[index],
                },
                {
                    yaw: positionData.yawPosition[index],
                    pitch: positionData.pitchPosition[index],
                    roll: positionData.rollPosition[index],
                }, "+=0"
            );

            animateVehiclePositionAndRotation(
                timeline,
                otherRef,
                {
                    x: positionDataOther.xPosition[index],
                    y: positionDataOther.yPosition[index],
                    z: positionDataOther.zPosition[index],
                },
                {
                    yaw: positionDataOther.yawPosition[index],
                    pitch: positionDataOther.pitchPosition[index],
                    roll: positionDataOther.rollPosition[index],
                }, "<"
            );
        });

        const timelineTotalDuration = timeline.totalDuration();
        const switchCamera1 = timelineTotalDuration / 3;
        const switchCamera2 = (2 * timelineTotalDuration) / 3;

        timeline.add('switchCamera1', switchCamera1);
        timeline.add('switchCamera2', switchCamera2);

        const durationToSwitchCamera1 = switchCamera1;
        const durationToSwitchCamera2 = switchCamera2 - switchCamera1;
        const durationToEnd = timelineTotalDuration - switchCamera2;

        if (scenarioLocation === "VMTC") {

            animateCamera(timeline, { x: -21, y: 15, z: 10 }, durationToSwitchCamera1, '0');
            animateCamera(timeline, { x: -25, y: 5, z: 7 }, 0, 'switchCamera1');
            animateCamera(timeline, { x: -29, y: 5, z: 6 }, durationToSwitchCamera2, 'switchCamera1');
            animateCamera(timeline, { x: -8, y: 6, z: 7 }, 0, 'switchCamera2');
            animateCamera(timeline, { x: -6, y: 6, z: 5.5 }, durationToEnd, 'switchCamera2');
        }
        else {
            animateCamera(timeline, { x: 11, y: 9, z: 1 }, durationToSwitchCamera1, '0');
            animateCamera(timeline, { x: -1, y: 5, z: -1 }, 0, 'switchCamera1');

            updateTarget(timeline, {
                x: (jaguarRef.current?.position?.x || 0)-10,
                y: jaguarRef.current?.position?.y || 0,
                z: jaguarRef.current?.position?.z || 0
            }, 'switchCamera1');
            animateCamera(timeline, { x: -10, y: 6, z: 3.6 }, durationToSwitchCamera2, 'switchCamera1');
            animateCamera(timeline, { x: 8.4, y: 6.4, z: -15 }, 0, 'switchCamera2');
            updateTarget(timeline, {
                x: (jaguarRef.current?.position?.x || 0) - 10,
                y: jaguarRef.current?.position?.y || 0,
                z: jaguarRef.current?.position?.z || 0
            }, 'switchCamera2');
            animateCamera(timeline, { x: 13, y: 6.7, z: -10.5 }, durationToEnd, 'switchCamera2');

        }
    }, [timeline, customCamera, controlsRef, jaguarRef, scenarioLocation]);



    useEffect(() => {
        if (jaguarRef.current && otherJaguarRef.current) {
            timeline.clear();
            animateVehicle(jaguarRef, otherJaguarRef, vehicle0Data, vehicle1Data);
        }
        return () => {
            timeline.kill();
        };
    }, [vehicle0Data, vehicle1Data, timeline, animateVehicle]);
    return (

        <React.Fragment >
            {/* <gridHelper></gridHelper> */}
            {/* <axesHelper args={[30]} /> */}

            <OrbitControls ref={controlsRef} maxPolarAngle={Math.PI / 2} target={initialCameraPosition} />
            <NeutralCar
                ref={otherJaguarRef}
                isTransparent={false}
                position={[vehicle1Data.xPosition[0], vehicle1Data.yPosition[0], vehicle1Data.zPosition[0]]}
                rotation={[vehicle1Data.yawPosition[0], vehicle1Data.pitchPosition[0], vehicle1Data.rollPosition[0]]}
            />
            <OpaJag
                ref={jaguarRef}
                isTransparent={false}
                position={[vehicle0Data.xPosition[0], vehicle0Data.yPosition[0], vehicle0Data.zPosition[0]]}
                rotation={[vehicle0Data.yawPosition[0], vehicle0Data.pitchPosition[0], vehicle0Data.rollPosition[0]]}
            />
            {scenarioLocation === "VMTC" ?
                (<Parking />) : (<ParkingUS />)}

            <ambientLight args={[0xffffff, 5]} />
            <directionalLight args={[0xffffff, 2]} position={[0, 0, 5]} />
            <directionalLight args={[0xffffff, 2]} position={[0, 0, -5]} />
            <directionalLight args={[0xffffff, 2]} position={[0, 5, 0]} />
            <directionalLight args={[0xffffff, 2]} position={[-5, 0, -5]} />
            <directionalLight args={[0xffffff, 2]} position={[5, 0, -5]} />

        </React.Fragment >
    );
});

export default React.memo(SceneViewer);
